import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";

import {getCourseLogos} from "../api/api";
import {CourseLogo} from "../types";
import {useTheme} from "@mui/material/styles";
import {themeLogoSvg} from "../helper/logo";

interface Props {
    value: number | null;
    onChange: (logoId: number | null) => void;
    disabled?: boolean;
    onError?: (message: string) => void;
}

const PAGE_SIZE = 50;

const getLogoSource = (content: string) => {
    if (content.startsWith("data:image")) {
        return content;
    }

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(content)}`;
};

const CourseLogoSelect = ({
                              value,
                              onChange,
                              disabled = false,
                              onError,
                          }: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const [logos, setLogos] = useState<CourseLogo[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const loadCourseLogos = async () => {
            setLoading(true);

            try {
                const firstPage = await getCourseLogos(0, PAGE_SIZE, "");

                const totalPages = Number.isFinite(firstPage.totalPages)
                    ? Math.max(firstPage.totalPages, 1)
                    : 1;

                let allLogos = [...firstPage.logos];

                if (totalPages > 1) {
                    const remainingRequests = Array.from(
                        {length: totalPages - 1},
                        (_, index) => getCourseLogos(index + 1, PAGE_SIZE, "")
                    );

                    const remainingPages = await Promise.all(remainingRequests);

                    allLogos = [
                        ...allLogos,
                        ...remainingPages.flatMap((response) => response.logos),
                    ];
                }

                if (!cancelled) {
                    setLogos(allLogos);
                }
            } catch (error: any) {
                if (!cancelled) {
                    setLogos([]);

                    onError?.(
                        error?.response?.data?.message ||
                        t("courseslist.errors.logoLoadFailed", {
                            defaultValue: "Failed to load course logos",
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadCourseLogos();

        return () => {
            cancelled = true;
        };
    }, [onError, t]);

    const selectedLogo = logos.find((logo) => logo.id === value);

    return (
        <FormControl fullWidth disabled={disabled || loading}>
            <InputLabel id="course-logo-label">
                {t("courseslist.courseLogo", {
                    defaultValue: "Course logo",
                })}
            </InputLabel>

            <Select
                labelId="course-logo-label"
                label={t("courseslist.courseLogo", {
                    defaultValue: "Course logo",
                })}
                value={value ?? ""}
                displayEmpty
                onChange={(event) => {
                    const selectedValue = event.target.value;

                    onChange(
                        selectedValue === ""
                            ? null
                            : Number(selectedValue)
                    );
                }}
                renderValue={() => {
                    if (loading) {
                        return (
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <CircularProgress size={18}/>

                                <Typography variant="body2">
                                    {t("common.loading", {
                                        defaultValue: "Loading...",
                                    })}
                                </Typography>
                            </Box>
                        );
                    }

                    if (!selectedLogo) {
                        return t("courseslist.defaultLogo", {
                            defaultValue: "",
                        });
                    }

                    return (
                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                            <Box
                                component="img"
                                src={getLogoSource(themeLogoSvg(selectedLogo.content, theme))}
                                alt={selectedLogo.name}
                                sx={{
                                    width: 30,
                                    height: 30,
                                    objectFit: "contain",
                                    flexShrink: 0,
                                }}
                            />

                            <Typography variant="body2">
                                {selectedLogo.name}
                            </Typography>
                        </Box>
                    );
                }}
            >
                <MenuItem value="">
                    <Typography>
                        {t("courseslist.defaultLogo", {
                            defaultValue: "",
                        })}
                    </Typography>
                </MenuItem>

                {logos.map((logo) => (
                    <MenuItem key={logo.id} value={logo.id}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                width: "100%",
                            }}
                        >
                            <Box
                                component="img"
                                src={getLogoSource(themeLogoSvg(logo.content, theme))}
                                alt={logo.name}
                                sx={{
                                    width: 34,
                                    height: 34,
                                    objectFit: "contain",
                                    flexShrink: 0,
                                }}
                            />

                            <Typography variant="body2">
                                {logo.name}
                            </Typography>
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default CourseLogoSelect;